// 📄 pages/MiEspacio/sections/TareasSection.tsx
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, ListChecks, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { Tarea, TareaData, PRIORIDADES_TAREA, CATEGORIAS_TAREA } from '../../../types/miEspacio.ts';
import { getTareas, crearTarea, updateTarea, toggleTarea, deleteTarea } from '../../../services/miEspacioService.ts';
import { MiEspacioSeccionProps, etiquetaContexto } from '../miEspacioContexto.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import TareaFormModal from '../components/TareaFormModal.tsx';

const inputClass = "bg-zinc-800 border border-white/10 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-blue-500";

const PRIORIDAD_STYLES: Record<string, string> = {
  Alta: 'bg-red-500/10 text-red-300 border-red-500/20',
  Media: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  Baja: 'bg-gray-500/10 text-gray-300 border-gray-500/20'
};
const PRIORIDAD_ORDEN: Record<string, number> = { Alta: 0, Media: 1, Baja: 2 };

type FiltroCompletado = 'todos' | 'pendientes' | 'completados';

const toDate = (value: any): Date | null => value?.toDate?.() || (value instanceof Date ? value : null);

const ordenar = (lista: Tarea[]): Tarea[] =>
  [...lista].sort((a, b) => {
    const fechaA = toDate(a.fechaLimite);
    const fechaB = toDate(b.fechaLimite);
    if (fechaA && fechaB) {
      const diff = fechaA.getTime() - fechaB.getTime();
      if (diff !== 0) return diff;
    } else if (fechaA && !fechaB) {
      return -1;
    } else if (!fechaA && fechaB) {
      return 1;
    }
    const prioDiff = (PRIORIDAD_ORDEN[a.prioridad || 'Media'] ?? 1) - (PRIORIDAD_ORDEN[b.prioridad || 'Media'] ?? 1);
    if (prioDiff !== 0) return prioDiff;
    return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
  });

export default function TareasSection({ contextosConsulta, contextoNuevo, proyectosPorId, mostrarProyecto }: MiEspacioSeccionProps) {
  const { user } = useAuth();
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [loading, setLoading] = useState(true);

  const [filtroCompletado, setFiltroCompletado] = useState<FiltroCompletado>('todos');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [completadosAbiertos, setCompletadosAbiertos] = useState(false);
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState<Tarea | null>(null);
  const [aEliminar, setAEliminar] = useState<Tarea | null>(null);

  const cargar = async () => {
    if (!user) return;
    setLoading(true);
    try {
      setTareas(await getTareas(user.uid, contextosConsulta));
    } catch (err) {
      console.error('Error cargando tareas:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [user?.uid, contextosConsulta.join(',')]);

  const toggleExpandida = (id: string) => {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openCreate = () => { setEditingTarea(null); setModalOpen(true); };
  const openEdit = (tarea: Tarea) => { setEditingTarea(tarea); setModalOpen(true); };

  const handleGuardar = async (data: TareaData) => {
    if (!user) return;
    if (editingTarea) {
      await updateTarea(user.uid, editingTarea.id, data, editingTarea.projectId);
    } else {
      await crearTarea(user.uid, data, contextoNuevo);
    }
    setModalOpen(false);
    cargar();
  };

  const handleToggle = async (tarea: Tarea) => {
    if (!user) return;
    setTareas((prev) => prev.map((t) => (t.id === tarea.id ? { ...t, completada: !t.completada } : t)));
    try {
      await toggleTarea(user.uid, tarea.id, !tarea.completada, tarea.projectId);
    } catch (err) {
      console.error('Error al actualizar la tarea:', err);
      cargar();
    }
  };

  const handleEliminar = async () => {
    if (!user || !aEliminar) return;
    try {
      await deleteTarea(user.uid, aEliminar.id, aEliminar.projectId);
      setTareas((prev) => prev.filter((t) => t.id !== aEliminar.id));
      setAEliminar(null);
    } catch (err) {
      console.error('Error al eliminar la tarea:', err);
    }
  };

  const filtradas = useMemo(() => {
    return tareas.filter((t) => {
      if (filtroPrioridad && (t.prioridad || 'Media') !== filtroPrioridad) return false;
      if (filtroCategoria && (t.categoria || 'Otro') !== filtroCategoria) return false;
      return true;
    });
  }, [tareas, filtroPrioridad, filtroCategoria]);

  const pendientes = ordenar(filtradas.filter((t) => !t.completada));
  const completadas = ordenar(filtradas.filter((t) => t.completada));

  const mostrarPendientes = filtroCompletado !== 'completados';
  const mostrarCompletadas = filtroCompletado !== 'pendientes';

  const renderItem = (tarea: Tarea) => {
    const fechaLimite = toDate(tarea.fechaLimite);
    const vencida = !!fechaLimite && !tarea.completada && fechaLimite.getTime() < Date.now();
    const expandida = expandidas.has(tarea.id);
    const prioridad = tarea.prioridad || 'Media';

    return (
      <div key={tarea.id} className={`bg-white/5 border border-white/10 rounded-xl px-4 py-3 ${tarea.completada ? 'opacity-60' : ''}`}>
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={tarea.completada}
            onChange={() => handleToggle(tarea)}
            className="w-4 h-4 rounded accent-blue-500 flex-shrink-0"
          />
          <span className={`flex-1 text-sm font-semibold ${tarea.completada ? 'text-gray-500 line-through' : 'text-white'}`}>{tarea.titulo}</span>

          <span className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${PRIORIDAD_STYLES[prioridad] || PRIORIDAD_STYLES.Media}`}>{prioridad}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-gray-300 flex-shrink-0">{tarea.categoria || 'Otro'}</span>

          {mostrarProyecto && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 flex-shrink-0">
              {etiquetaContexto(tarea.projectId, proyectosPorId)}
            </span>
          )}

          {tarea.notas && (
            <button onClick={() => toggleExpandida(tarea.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 flex-shrink-0" title="Ver notas">
              {expandida ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
          <button onClick={() => openEdit(tarea)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 flex-shrink-0" title="Editar">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={() => setAEliminar(tarea)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 flex-shrink-0" title="Eliminar">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {fechaLimite && (
          <p className={`text-[11px] mt-1.5 ml-7 ${vencida ? 'text-red-400 font-semibold' : 'text-gray-500'}`}>
            Vence el {fechaLimite.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </p>
        )}

        {expandida && tarea.notas && (
          <p className="text-xs text-gray-300 mt-2 ml-7 whitespace-pre-wrap bg-black/20 rounded-lg p-2.5">{tarea.notas}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-zinc-800 border border-white/10 rounded-lg p-0.5 text-xs">
            {(['todos', 'pendientes', 'completados'] as FiltroCompletado[]).map((f) => (
              <button
                key={f}
                onClick={() => setFiltroCompletado(f)}
                className={`px-3 py-1.5 rounded-md font-medium transition-all ${filtroCompletado === f ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                {f === 'todos' ? 'Todos' : f === 'pendientes' ? 'Pendientes' : 'Completados'}
              </button>
            ))}
          </div>
          <select className={inputClass} value={filtroPrioridad} onChange={(e) => setFiltroPrioridad(e.target.value)}>
            <option value="">Toda prioridad</option>
            {PRIORIDADES_TAREA.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className={inputClass} value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
            <option value="">Toda categoría</option>
            {CATEGORIAS_TAREA.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
          <Plus className="w-4 h-4" /> Agregar pendiente
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Cargando pendientes...</p>
      ) : filtradas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <ListChecks className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">No hay pendientes que coincidan con el filtro.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {mostrarPendientes && pendientes.length > 0 && (
            <div className="space-y-2">{pendientes.map(renderItem)}</div>
          )}

          {mostrarCompletadas && completadas.length > 0 && (
            filtroCompletado === 'completados' ? (
              <div className="space-y-2">{completadas.map(renderItem)}</div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => setCompletadosAbiertos((v) => !v)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white uppercase tracking-wide"
                >
                  {completadosAbiertos ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  Completados ({completadas.length})
                </button>
                {completadosAbiertos && <div className="space-y-2">{completadas.map(renderItem)}</div>}
              </div>
            )
          )}
        </div>
      )}

      {modalOpen && (
        <TareaFormModal tarea={editingTarea} onClose={() => setModalOpen(false)} onSave={handleGuardar} />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este pendiente?"
          message={`"${aEliminar.titulo}" se eliminará permanentemente.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
